import { datadogRum } from '@datadog/browser-rum';
import lodash from 'lodash';
import React from 'react';
import { useCookies } from 'react-cookie';
import awsRum from '../../../../../awsRum';
import config from '../../../../../config';
import { useGetOrganisationQuery } from '../../../../../generated/core-api/graphql-react-query';
import { getAudioInputDevices } from './getAudioInputDevices';

let devicePollInterval: NodeJS.Timeout | null = null;
let lastDevices: string[] = [];

async function checkNewDevices() {
  const devices = await getAudioInputDevices();
  const ids = devices.map((device) => device.deviceId);
  const result = !lodash.isEqual(ids, lastDevices);
  if (result) {
    lastDevices = ids;
  }
  return result;
}

export interface AudioConfig {
  permissionStatus: PermissionState | null;
  devices: MediaDeviceInfo[];
  deviceId: string | null;
  setDeviceId: (id: string | null) => void;
  noiseSuppression?: boolean;
  setNoiseSuppression: (value: boolean) => void;
  echoCancellation?: boolean;
  setEchoCancellation: (value: boolean) => void;
  autoGainControl?: boolean;
  setAutoGainControl: (value: boolean) => void;
}

export default function useAudioConfig(): AudioConfig {
  const [cookies, setCookies, clearCookies] = useCookies(['liveDeviceId', 'audioNoiseSuppression', 'audioEchoCancellation', 'audioAutoGainControl']);
  const [permissionStatus, setPermissionStatus] = React.useState<null | PermissionState>(null);
  const [deviceId, setDeviceId] = React.useState<string | null>(null);
  const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
  const orgSettings = useGetOrganisationQuery().data?.org;

  const favoriteDeviceId = React.useMemo(() => {
    const id = cookies.liveDeviceId;
    const device = devices.find((d) => d.deviceId === id);
    return device?.deviceId;
  }, [cookies.liveDeviceId, devices]);

  const noiseSuppression = React.useMemo(() => {
    if (cookies.audioNoiseSuppression === 'true') return true;
    if (cookies.audioNoiseSuppression === 'false') return false;
    return orgSettings?.defaultAudioSettings_enableAudioNoiseSuppression;
  }, [cookies.audioNoiseSuppression, orgSettings?.defaultAudioSettings_enableAudioNoiseSuppression]);

  const echoCancellation = React.useMemo(() => {
    if (cookies.audioEchoCancellation === 'true') return true;
    if (cookies.audioEchoCancellation === 'false') return false;
    return orgSettings?.defaultAudioSettings_enableAudioEchoCancellation;
  }, [cookies.audioNoiseSuppression, orgSettings?.defaultAudioSettings_enableAudioEchoCancellation]);

  const autoGainControl = React.useMemo(() => {
    if (cookies.audioAutoGainControl === 'true') return true;
    if (cookies.audioAutoGainControl === 'false') return false;
    return orgSettings?.defaultAudioSettings_enableAudioAutoGainControl;
  }, [cookies.audioNoiseSuppression, orgSettings?.defaultAudioSettings_enableAudioAutoGainControl]);

  React.useEffect(() => {
    awsRum?.addSessionAttributes({ liveDeviceId: deviceId || 'null' });
    datadogRum.setUserProperty('liveDeviceId', deviceId || 'null');
  }, [deviceId]);

  function changeDeviceId(id: string | null) {
    setDeviceId(id);
    if (id) {
      setCookies(
        'liveDeviceId',
        id,
        {
          path: '/', maxAge: 60 * 60 * 24 * 365, secure: true, sameSite: 'strict',
        },
      );
    } else clearCookies('liveDeviceId', { path: '/' });
  }

  React.useEffect(() => {
    navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor).then((status) => {
      setPermissionStatus(status.state);
    });
  }, []);

  const deviceChangeListener = React.useCallback(async () => {
    const currentDevices = await getAudioInputDevices();
    setDevices(currentDevices);
    if (currentDevices.length === 0) {
      setDeviceId(null);
    } else if (!currentDevices.find((device) => device.deviceId === deviceId)) {
      setDeviceId(currentDevices[0].deviceId);
    }
  }, [deviceId]);

  React.useEffect(() => {
    switch (permissionStatus) {
      case 'granted':
        deviceChangeListener();
        navigator.mediaDevices.ondevicechange = () => {
          deviceChangeListener();
        };
        devicePollInterval = setInterval(async () => {
          if (await checkNewDevices()) {
            deviceChangeListener();
          }
        }, config.Live.Audio.Devices.FallbackInterval);
        break;
      case 'prompt':
        if (devicePollInterval) clearInterval(devicePollInterval);
        devicePollInterval = null;
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then((stream) => {
            stream.getAudioTracks().forEach((track) => {
              track.stop();
            });
          })
          .finally(() => {
            navigator.permissions.query({ name: 'microphone' } as any).then((status) => {
              setPermissionStatus(status.state);
            });
          });
        break;
      default:
        if (devicePollInterval) clearInterval(devicePollInterval);
        devicePollInterval = null;
        break;
    }
  }, [deviceChangeListener, permissionStatus]);

  return {
    permissionStatus,
    devices,
    deviceId: favoriteDeviceId ?? deviceId,
    setDeviceId: changeDeviceId,
    noiseSuppression,
    setNoiseSuppression: (value: boolean) => {
      setCookies('audioNoiseSuppression', value.toString(), {
        path: '/', maxAge: 60 * 60 * 24 * 365, secure: true, sameSite: 'strict',
      });
    },
    echoCancellation,
    setEchoCancellation: (value: boolean) => {
      setCookies('audioEchoCancellation', value.toString(), {
        path: '/', maxAge: 60 * 60 * 24 * 365, secure: true, sameSite: 'strict',
      });
    },
    autoGainControl,
    setAutoGainControl: (value: boolean) => {
      setCookies('audioAutoGainControl', value.toString(), {
        path: '/', maxAge: 60 * 60 * 24 * 365, secure: true, sameSite: 'strict',
      });
    },
  };
}
